import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Mail, MessageCircle, Shield, Package, Palette, Headphones, Star, ChevronDown } from 'lucide-react';

const FAQPage = () => {
  const [activeQuestion, setActiveQuestion] = useState(0);

  const faqData = [
    {
      question: "What if the product doesn't fit me?",
      icon: Package,
      answer: "No worries at all! We offer free returns on all items within 30 days of purchase. Simply contact our support team and we'll send you a prepaid return label. If you need a different size or color, we can also arrange a free exchange. After we receive your return, we'll process your refund within 3-5 business days to your original payment method."
    },
    {
      question: "What guarantees do you provide?",
      icon: Shield,
      answer: "We stand behind every product we sell with a comprehensive satisfaction guarantee. All items come with a 30-day money-back guarantee, manufacturer warranties where applicable, and our quality promise. If you're not completely satisfied with your purchase, we'll make it right - whether that's a replacement, exchange, or full refund."
    },
    {
      question: "I don't know how many items to order. What should I do?",
      icon: Star,
      answer: "Start with our recommended starter packs or sample sizes! Our customer service team can help you determine the right quantities based on your needs. We also offer flexible ordering - you can always place additional orders later. Many customers start small and then reorder their favorites. Plus, we offer bulk discounts for larger quantities if you decide you love our products."
    },
    {
      question: "I want to order a custom product and specify what I want. Is this possible?",
      icon: Palette,
      answer: "Absolutely! We love creating custom orders for our customers. Simply reach out to our design team with your specifications, and we'll work with you to create exactly what you envision. Custom orders typically take 7-14 business days to complete. We'll send you a proof for approval before production begins, and we offer unlimited revisions until you're completely happy with the design."
    }
  ];

  const toggleQuestion = (index) => {
    setActiveQuestion(activeQuestion === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const answerVariants = {
    hidden: { 
      height: 0, 
      opacity: 0,
      paddingTop: 0,
      paddingBottom: 0
    },
    visible: { 
      height: "auto", 
      opacity: 1,
      paddingTop: "1.5rem",
      paddingBottom: "1.5rem",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      height: 0,
      opacity: 0,
      paddingTop: 0,
      paddingBottom: 0,
      transition: {
        duration: 0.3,
        ease: "easeIn"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 mt-20">
      <motion.div 
        className="max-w-4xl mx-auto mt-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Simple Header */}
        <motion.div 
          className="text-center mb-12"
          variants={itemVariants}
        >
          <motion.div 
            className="flex items-center justify-center gap-3 mb-6"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-2xl text-gray-800">8</span>
            <span className="text-sm text-gray-500">out of</span>
            <span className="text-2xl text-gray-800">10</span>
          </motion.div>
          
          <h1 className="text-xl text-gray-900 mb-3">
            Frequently Asked Questions
          </h1>
          
          <p className="text-gray-600 text-sm max-w-lg mx-auto">
            Common questions from our customers with detailed answers
          </p>
        </motion.div>

        {/* FAQ Items */}
        <motion.div className="space-y-3">
          {faqData.map((faq, index) => {
            const IconComponent = faq.icon;
            return (
              <motion.div
                key={index}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-orange-300 transition-colors duration-200"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.002
                }}
                transition={{ duration: 0.2 }}
              >
                <motion.button
                  onClick={() => toggleQuestion(index)}
                  className="w-full p-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200 group"
                  whileTap={{ scale: 0.995 }}
                >
                  <div className="flex items-center gap-4">
                    <motion.div 
                      className="bg-gray-100 p-2 rounded-md group-hover:bg-orange-100 transition-colors duration-200"
                      whileHover={{ scale: 1.05 }}
                    >
                      <IconComponent className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors duration-200" />
                    </motion.div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-lg text-gray-300 min-w-[1.5rem] group-hover:text-orange-400 transition-colors duration-200">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-sm text-gray-800 pr-4">
                        {faq.question}
                      </h3>
                    </div>
                  </div>
                  
                  <motion.div
                    className="bg-gray-100 p-2 rounded-md flex-shrink-0 group-hover:bg-orange-100 transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      animate={{ rotate: activeQuestion === index ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <ChevronDown className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors duration-200" />
                    </motion.div>
                  </motion.div>
                </motion.button>
                
                <AnimatePresence>
                  {activeQuestion === index && (
                    <motion.div
                      variants={answerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="px-5 bg-gray-50 overflow-hidden border-t border-gray-100"
                    >
                      <motion.div 
                        className="pl-12 pr-4"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.3 }}
                      >
                        <p className="text-gray-700 leading-relaxed text-sm">
                          {faq.answer}
                        </p>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Simple Contact Section */}
        <motion.div 
          className="mt-12 text-center"
          variants={itemVariants}
        >
          <motion.div
            className="bg-white border border-gray-200 rounded-lg p-8"
            whileHover={{ 
              borderColor: "#fb923c"
            }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="flex items-center justify-center mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <div className="bg-orange-100 p-3 rounded-lg">
                <Headphones className="w-5 h-5 text-orange-600" />
              </div>
            </motion.div>
            
            <h2 className="text-lg text-gray-900 mb-3">
              Need more help?
            </h2>
            <p className="text-gray-600 mb-6 text-sm max-w-sm mx-auto">
              Our support team is available to assist you
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <motion.button
                className="bg-orange-500 text-white px-6 py-3 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors duration-200"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Mail className="w-4 h-4" />
                Email Support
              </motion.button>
              
              <motion.button
                className="border border-orange-500 text-orange-600 px-6 py-3 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-orange-50 transition-colors duration-200"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <MessageCircle className="w-4 h-4" />
                Live Chat
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FAQPage;